import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinearProgress from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ServiceTypeCostRow, CostBreakdownRow } from '../Model/CostType';
import ScopeBadge from './ScopeBadge';
import { costService } from '../Services/CostService';
import {
  fmtFull,
  NOK,
  CurrencyConfig,
} from '../../../Components/Layout/SqlServer/Common/util';

interface ServiceTypeTabProps {
  serviceTypeSummary: ServiceTypeCostRow[];
  currency?: CurrencyConfig;
  baseCurrency?: string;
  filter: { from: string; to: string };
}

const ServiceTypeTab: React.FC<ServiceTypeTabProps> = ({
  serviceTypeSummary,
  currency = NOK,
  baseCurrency = 'NOK',
  filter,
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [details, setDetails] = useState<Record<string, CostBreakdownRow[]>>(
    {},
  );
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const handleExpand =
    (serviceType: string) =>
    async (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? serviceType : false);

      if (isExpanded && !details[serviceType] && !loading[serviceType]) {
        setLoading((prev) => ({ ...prev, [serviceType]: true }));
        try {
          const res = await costService.getServiceBreakdown(
            serviceType,
            filter.from,
            filter.to,
          );
          setDetails((prev) => ({ ...prev, [serviceType]: res }));
        } catch (err) {
          console.error('Failed to load service details', err);
        } finally {
          setLoading((prev) => ({ ...prev, [serviceType]: false }));
        }
      }
    };

  const maxCost = serviceTypeSummary[0]?.Cost ?? 1;

  return (
    <Box sx={{ mt: 1 }}>
      {serviceTypeSummary.map((s) => (
        <Accordion
          key={s.ServiceType}
          expanded={expanded === s.ServiceType}
          onChange={handleExpand(s.ServiceType)}
          disableGutters
          elevation={0}
          sx={{
            mb: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 52,
              px: 2,
              bgcolor: expanded === s.ServiceType ? 'action.hover' : 'inherit',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                pr: 2,
              }}
            >
              <Box sx={{ minWidth: 160 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {s.ServiceType}
                </Typography>
              </Box>

              <Box sx={{ width: 80, ml: 2 }}>
                <ScopeBadge scope={s.Scope} />
              </Box>

              <Box sx={{ flexGrow: 1, mx: 4 }}>
                <LinearProgress
                  variant="determinate"
                  value={(s.Cost / maxCost) * 100}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'action.selected',
                    '& .MuiLinearProgress-bar': { borderRadius: 2 },
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  minWidth: 100,
                  textAlign: 'right',
                }}
              >
                {fmtFull(s.Cost, currency, baseCurrency)}
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails
            sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider' }}
          >
            {loading[s.ServiceType] ? (
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((l) => (
                  <Skeleton key={l} height={32} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                      Resource
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                      Tenant
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: 12 }}
                    >
                      Cost
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details[s.ServiceType]?.map((r) => (
                    <TableRow key={r.ResourceId} hover>
                      <TableCell sx={{ py: 1 }}>
                        <Typography
                          sx={{ fontSize: 13, color: 'text.primary' }}
                        >
                          {r.ResourceName || 'Unknown Resource'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          {r.ResourceType}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: 12, color: 'info.main' }}>
                          {r.Tenant || '(Unknown)'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontFamily: 'monospace',
                            fontWeight: 600,
                          }}
                        >
                          {fmtFull(r.Cost, currency, baseCurrency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!details[s.ServiceType] ||
                    details[s.ServiceType].length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No resource details available for this period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ServiceTypeTab;
